import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DashboardTabs } from "@/components/dashboard/DashboardTabs";
import { DashboardConfigurator } from "@/components/dashboard/DashboardConfigurator";
import { useUserStore } from "@/store/userStore";
import { useDashboardConfigStore } from "@/store/dashboardConfigStore";
import { Button } from "@/components/ui/button";
import { Settings, X, Check, LayoutGrid } from "lucide-react";
import { toast } from "sonner";


export default function Dashboard() {
  const { t } = useTranslation(["dashboard", "adminui2"]);
  const { isLoadingGymSwitch, gymId } = useUserStore();
  const { isEditMode, setEditMode, cancelEdit, saveConfig, isSaving } = useDashboardConfigStore();
  const [configuratorOpen, setConfiguratorOpen] = useState(false);

  // « Terminer » n'appelait que `setEditMode(false)` : la disposition n'était
  // jamais envoyée au serveur, et « Annuler » ne restaurait rien.
  const handleFinishEdit = async () => {
    if (!gymId) {
      toast.error(t("adminui2:dashboardPage.errorNoGym"));
      return;
    }
    const success = await saveConfig(gymId);
    if (success) {
      setEditMode(false);
      toast.success(t("adminui2:dashboardPage.savedToast"));
    } else {
      // Pas de toast de succès sans confirmation du serveur : la disposition
      // reste appliquée localement, mais elle n'est pas enregistrée.
      toast.error(t("adminui2:dashboardPage.notSavedToast"));
    }
  };

  if (isLoadingGymSwitch) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-3 sm:space-y-6 sm:p-6">
      {/* Header with customize button */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold sm:text-3xl">{t("title")}</h1>
          <p className="mt-1 max-w-xl text-xs text-muted-foreground sm:text-sm">{t("subtitle")}</p>
        </div>
        
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {isEditMode ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelEdit}
                disabled={isSaving}
              >
                <X className="h-4 w-4 mr-2" />
                {t("adminui2:dashboardPage.cancel")}
              </Button>
              <Button
                size="sm"
                onClick={handleFinishEdit}
                disabled={isSaving}
              >
                <Check className="h-4 w-4 mr-2" />
                {isSaving ? t("adminui2:dashboardPage.saving") : t("adminui2:dashboardPage.finish")}
              </Button>
            </>
          ) : (
            <>
              {/* Aucun bouton n'activait le mode édition : on n'y entrait que
                  par l'état persisté (le bug « retour en mode édition après
                  rechargement »). Le glisser-déposer était donc inatteignable
                  et les boutons Terminer/Annuler du mode édition, morts. */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditMode(true)}
              >
                <LayoutGrid className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t("adminui2:dashboardPage.reorganize")}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfiguratorOpen(true)}
              >
                <Settings className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t("adminui2:dashboardPage.customize")}</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Edit mode banner */}
      {isEditMode && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
            <Settings className="h-4 w-4 text-primary animate-spin" style={{ animationDuration: '3s' }} />
          </div>
          <div>
            <p className="font-medium text-sm">{t("adminui2:dashboardPage.editModeTitle")}</p>
            <p className="text-xs text-muted-foreground">
              {t("adminui2:dashboardPage.editModeDesc")}
            </p>
          </div>
        </div>
      )}

      <DashboardTabs />

      {/* Configurator Sheet */}
      <DashboardConfigurator 
        open={configuratorOpen} 
        onOpenChange={setConfiguratorOpen} 
      />
    </div>
  );
}
